import { useAuthStore } from '@/store/auth.store';
import { Navigate, useLocation } from 'react-router-dom';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** If true, allow vault_user access to this route */
  allowVaultUser?: boolean;
  /** If true, skip the 2FA setup redirect check */
  skipForce2FA?: boolean;
  /** If true, only vault_user can access (blocks managers/regular users) */
  vaultUserOnly?: boolean;
}

/**
 * ProtectedRoute
 *
 * Handles authentication and access control:
 * 1. Redirects to /login if not authenticated
 * 2. Redirects to /setup-2fa if requires2FASetup && !twoFactorEnabled
 * 3. Redirects to /vault/initialize/:id if pendingVaultId
 * 4. Redirects to /my-vaults if vaultOnlyAccess and not on allowed routes
 * 5. Redirects to /folders if vaultUserOnly and user is not a vault_user (blocks managers)
 */
export const ProtectedRoute = ({
  children,
  allowVaultUser = false,
  skipForce2FA = false,
  vaultUserOnly = false,
}: ProtectedRouteProps) => {
  const { isAuthenticated, user } = useAuthStore();
  const location = useLocation();

  // 1. Check authentication
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Routes that are always allowed for vault users
  const vaultUserAllowedPaths = [
    '/setup-2fa',
    '/vault/initialize',
    '/vault', // Allow vault detail page access
    '/my-vaults',
    '/my-vault',
    '/support',
    '/logout',
  ];

  // Check if current path is allowed for vault users
  const isVaultUserAllowedPath = vaultUserAllowedPaths.some(
    (path) =>
      location.pathname === path || location.pathname.startsWith(`${path}/`)
  );

  // 2. Check if 2FA setup is required (for vault users)
  // Allow access if TOTP is enabled OR if OTP (email) method is configured
  if (
    !skipForce2FA &&
    user?.requires2FASetup &&
    !user?.twoFactorEnabled &&
    user?.securityMethod !== 'otp' &&
    location.pathname !== '/setup-2fa'
  ) {
    return <Navigate to="/setup-2fa" replace />;
  }

  // 3. Check if vault initialization is pending
  if (
    user?.pendingVaultId &&
    !location.pathname.startsWith('/vault/initialize') &&
    !location.pathname.startsWith('/setup-2fa')
  ) {
    return (
      <Navigate to={`/vault/initialize/${user.pendingVaultId}`} replace />
    );
  }

  // 4. Check if user is vault_only and trying to access main dashboard
  if (
    user?.vaultOnlyAccess &&
    !allowVaultUser &&
    !isVaultUserAllowedPath
  ) {
    return <Navigate to="/my-vaults" replace />;
  }

  // 5. Check if route is vault_user only and user is a manager (not vault_user)
  // Managers should not access vault content pages directly
  if (vaultUserOnly && !user?.vaultOnlyAccess) {
    return <Navigate to="/folders" replace />;
  }

  return <>{children}</>;
};
